import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useFocusEffect } from 'expo-router';

// React Native Reusables Components
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { PageHeader } from '@/design-system/components';

// Services & Types
import { adminRAGAPI, authAPI } from '../../services/api';
import { Document, CollectionStats } from '../../types';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import { trackAuthEvent } from '../../services/analytics';
import { actionErrorMessage } from '../chat/chatErrors';
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#DC2626',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
};

/**
 * Enhanced Documents Screen - Professional Design
 *
 * - Professional design tokens (COLORS) for consistent styling
 * - Complete i18n support with no hardcoded text
 * - React Native Reusables components for consistent UI
 * - Proper safe area handling for iOS
 * - Clean, modern document management interface
 */
export default function DocumentsScreenEnhanced() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const insets = useSafeAreaInsets();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [stats, setStats] = useState<CollectionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const latestDocsCountRef = useRef(0);
  const latestIsAdminRef = useRef(false);

  useEffect(() => {
    latestDocsCountRef.current = documents.length;
  }, [documents.length]);

  useEffect(() => {
    latestIsAdminRef.current = isAdmin;
  }, [isAdmin]);

  const getFileExtension = (name?: string) => {
    if (!name) return undefined;
    const parts = name.split('.');
    if (parts.length < 2) return undefined;
    return parts.pop()?.toLowerCase();
  };

  const loadDocuments = useCallback(async () => {
    try {
      const docs = await adminRAGAPI.listKnowledge();
      setDocuments(docs);
    } catch {
      showDialog(t('documents.loadFailed'));
    }
  }, [t]);

  const loadStats = useCallback(async () => {
    try {
      const collectionStats = await adminRAGAPI.getStats();
      setStats(collectionStats);
    } catch (error) {
      console.log('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAdminAccess = useCallback(async () => {
    try {
      const user = await authAPI.getCurrentUser();
      const adminAccess = user.role === 'admin';
      setIsAdmin(adminAccess);

      if (adminAccess) {
        loadDocuments();
        loadStats();
      } else {
        trackAuthEvent('documents_access_denied', { role: user.role });
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to check user role:', error);
      setLoading(false);
    }
  }, [loadDocuments, loadStats]);

  useEffect(() => {
    checkAdminAccess();
  }, [checkAdminAccess]);

  useFocusEffect(
    React.useCallback(() => {
      trackAuthEvent('documents_screen_viewed', {
        is_admin: latestIsAdminRef.current,
        document_count: latestDocsCountRef.current,
      });
      if (latestIsAdminRef.current) {
        loadDocuments();
        loadStats();
      }
    }, [loadDocuments, loadStats])
  );

  const pickDocument = async () => {
    try {
      trackAuthEvent('document_upload_initiated', { source: 'documents_screen' });
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        await uploadDocument(file);
      } else if (result.canceled) {
        trackAuthEvent('document_upload_cancelled', { source: 'documents_screen' });
      }
    } catch {
      trackAuthEvent('document_upload_failed', { error_type: 'picker_error' });
      showDialog(t('common.error'), t('documents.uploadFailed'));
    }
  };

  const uploadDocument = async (file: any) => {
    setUploading(true);
    const fileMeta = {
      file_type: file?.mimeType || 'application/octet-stream',
      file_ext: getFileExtension(file?.name),
      file_size: file?.size,
      source: 'documents_screen',
    };
    try {
      const fileData = {
        uri: file.uri,
        type: file.mimeType || 'application/octet-stream',
        name: file.name,
      };

      trackAuthEvent('document_upload_started', fileMeta);
      const response = await adminRAGAPI.uploadKnowledge(fileData, 'general', 'coaching_material');
      const createdChunks = Number((response as any)?.chunks_created ?? (response as any)?.chunks ?? (response as any)?.count ?? 0);

      loadDocuments();
      loadStats();

      trackAuthEvent('document_uploaded', {
        ...fileMeta,
        chunks_created: createdChunks,
      });
      showToast(`${t('documents.uploadSuccess')}\n${t('documents.chunksCreated')}: ${createdChunks}`, 'success');
    } catch (error: any) {
      const errorStatus = error?.response?.status;
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('document_upload_failed', {
        ...fileMeta,
        error_type: errorType,
      });
      // CHAT-19: translated text only (was "HTTP undefined: ..." on network errors).
      showDialog(actionErrorMessage(t, error, 'documents.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (documentId: number, filename: string) => {
    const confirmed = await confirmDialog(
      t('common.delete'),
      `${t('documents.confirmDelete')} "${filename}"?`,
      t('common.delete'),
      t('common.cancel'),
    );

    if (confirmed) {
      try {
        trackAuthEvent('document_delete_confirmed', { document_id: documentId });
        await adminRAGAPI.deleteKnowledge(documentId);
        showToast(t('documents.deleteSuccess'), 'success');
        trackAuthEvent('document_deleted', { document_id: documentId });
        loadDocuments();
        loadStats();
      } catch {
        trackAuthEvent('document_delete_failed', { document_id: documentId });
        showDialog(t('documents.deleteFailed'));
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const renderDocument = ({ item }: { item: Document }) => (
    <Card className="mb-3">
      <CardContent className="p-4">
        <View className="flex-row items-center mb-3">
          <View
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: `${COLORS.primary}15` }}
          >
            <MaterialIcons
              name={item.content_type === 'application/pdf' ? 'picture-as-pdf' : 'description'}
              size={24}
              color={COLORS.primary}
            />
          </View>
          <View className="flex-1">
            <Text className="text-base font-semibold text-foreground" numberOfLines={1}>
              {item.filename}
            </Text>
            <Text className="text-sm text-muted-foreground">
              {formatFileSize(item.file_size)} • {item.chunk_count} {t('documents.chunks')} • {formatDate(item.created_at)}
            </Text>
          </View>
          <TouchableOpacity
            onPress={() => deleteDocument(item.id, item.filename)}
            className="p-2"
          >
            <MaterialIcons name="delete" size={20} color={COLORS.error} />
          </TouchableOpacity>
        </View>

        <View className="flex-row gap-2">
          <View className="px-3 py-1 rounded-full" style={{ backgroundColor: `${COLORS.primary}15` }}>
            <Text className="text-xs font-semibold" style={{ color: COLORS.primary }}>
              {item.coaching_method}
            </Text>
          </View>
          <View
            className="px-3 py-1 rounded-full"
            style={{ backgroundColor: item.processed ? `${COLORS.success}15` : `${COLORS.secondary}15` }}
          >
            <Text
              className="text-xs font-semibold"
              style={{ color: item.processed ? COLORS.success : COLORS.secondary }}
            >
              {item.processed ? t('documents.processed') : t('documents.processing')}
            </Text>
          </View>
        </View>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <SafeAreaView className="flex-1 bg-surface items-center justify-center">
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text className="text-base text-muted-foreground mt-4">{t('documents.loading')}</Text>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView className="flex-1 bg-surface">
        <ScrollView
          contentContainerStyle={{
            padding: 16,
            paddingBottom: Math.max(insets.bottom + 16, 32),
          }}
        >
          <Card className="bg-blue-50">
            <CardContent className="p-8 items-center">
              <MaterialIcons name="admin-panel-settings" size={64} color={COLORS.secondary} />
              <Text className="text-2xl font-bold text-foreground mt-4 mb-2 text-center">
                {t('documents.accessDeniedTitle')}
              </Text>
              <Text className="text-base text-muted-foreground text-center mb-2">
                {t('documents.accessDeniedText')}
              </Text>
              <Text className="text-sm text-muted-foreground text-center mb-6">
                {t('documents.accessDeniedSubtext')}
              </Text>
              <View
                className="p-4 rounded-lg flex-row items-start"
                style={{ backgroundColor: '#E3F2FD', borderLeftWidth: 4, borderLeftColor: COLORS.primary }}
              >
                <MaterialIcons name="info" size={20} color={COLORS.primary} />
                <Text className="text-sm ml-2 flex-1" style={{ color: '#1976D2' }}>
                  {t('documents.accessDeniedInfo')}
                </Text>
              </View>
            </CardContent>
          </Card>
        </ScrollView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-surface">
      <PageHeader
        title={t('documents.title')}
        icon="folder"
        backgroundColor="white"
        textColor={COLORS.text.primary}
      />

      <ScrollView
        contentContainerStyle={{
          paddingTop: 16,
          paddingBottom: Math.max(insets.bottom + 100, 120),
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Max-width container for better UX on large screens */}
        <View className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Subtitle */}
        {t('documents.subtitle') && (
          <Card className="mb-4" style={{ backgroundColor: 'rgba(30, 64, 175, 0.05)' }}>
            <CardContent className="p-4">
              <Text className="text-sm text-center" style={{ color: COLORS.primary }}>
                {t('documents.subtitle')}
              </Text>
            </CardContent>
          </Card>
        )}

        {/* Stats Card */}
        {stats && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <Text className="text-lg font-bold text-foreground mb-3">
                {t('documents.knowledgeBaseStats')}
              </Text>
              <View className="flex-row justify-between">
                <View>
                  <Text className="text-sm text-muted-foreground">{t('documents.totalChunks')}</Text>
                  <Text className="text-2xl font-bold text-foreground">{stats.total_chunks}</Text>
                </View>
                <View>
                  <Text className="text-sm text-muted-foreground">{t('documents.status')}</Text>
                  <Text className="text-base font-semibold text-foreground">{stats.status}</Text>
                </View>
              </View>
            </CardContent>
          </Card>
        )}

        {/* Upload Button */}
        <TouchableOpacity
          onPress={pickDocument}
          disabled={uploading}
          className="flex-row items-center justify-center gap-2 rounded-lg py-4 mb-4"
          style={{
            backgroundColor: uploading ? '#93C5FD' : COLORS.primary,
          }}
        >
          {uploading ? (
            <ActivityIndicator color="white" />
          ) : (
            <MaterialIcons name="cloud-upload" size={24} color="white" />
          )}
          <Text className="text-base font-bold text-white">
            {uploading ? t('documents.uploading') : t('documents.uploadButton')}
          </Text>
        </TouchableOpacity>

        {/* Documents List */}
        {documents.length === 0 ? (
          <Card>
            <CardContent className="p-8 items-center">
              <MaterialIcons name="folder-open" size={64} color="#ccc" />
              <Text className="text-lg font-bold text-muted-foreground mt-4 text-center">
                {t('documents.noDocuments')}
              </Text>
              <Text className="text-sm text-muted-foreground mt-2 text-center">
                {t('documents.noDocumentsSubtext')}
              </Text>
            </CardContent>
          </Card>
        ) : (
          <View>
            {documents.map((item) => (
              <View key={item.id}>{renderDocument({ item })}</View>
            ))}
          </View>
        )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
